import { useCallback, useEffect, useRef, useState } from "react";
import { listManufacturingPricingLayersGrid } from "@/lib/manufacturingClient";
import type { MfgPricingLayerColumn, MfgPricingLayerGridRow } from "@/lib/manufacturing/types";

const CATALOG_PAGE = 2000;

type Args = {
  enabled: boolean;
  navTick: number;
  plantId: string;
  includeMaterialUses: boolean;
  includeNamedVariants: boolean;
};

/**
 * One server snapshot for the current plant. Group, style, size, and paging
 * stay in the browser until the plant, uses, or variants change.
 */
export function useCogsCatalogMemory(args: Args) {
  const [rows, setRows] = useState<MfgPricingLayerGridRow[]>([]);
  const [columns, setColumns] = useState<MfgPricingLayerColumn[]>([]);
  const [series, setSeries] = useState<string[]>([]);
  const [roofStyles, setRoofStyles] = useState<string[]>([]);
  const [sizeBands, setSizeBands] = useState<string[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const requestId = useRef(0);

  const reload = useCallback(async () => {
    if (!args.enabled) return;
    const my = ++requestId.current;
    setListBusy(true);
    setLocalError(null);
    setRows([]);
    try {
      const products: MfgPricingLayerGridRow[] = [];
      const extras: MfgPricingLayerGridRow[] = [];
      let offset = 0;
      let total = 0;
      let first = true;
      do {
        const r = await listManufacturingPricingLayersGrid({
          plant_id: args.plantId || undefined,
          include_material_uses: offset === 0 ? args.includeMaterialUses : false,
          include_named_variants: offset === 0 ? args.includeNamedVariants : false,
          catalog: true,
          limit: CATALOG_PAGE,
          offset,
        });
        if (requestId.current !== my) return;
        if (!r.ok) {
          setLocalError(r.error);
          return;
        }
        if (first) {
          setColumns(r.data.columns);
          setSeries(r.data.filter_options?.series ?? []);
          setRoofStyles(r.data.filter_options?.roof_styles ?? []);
          setSizeBands(r.data.filter_options?.size_bands ?? []);
          first = false;
        }
        for (const row of r.data.rows) {
          if (row.subject_kind === "recipe") products.push(row);
          else extras.push(row);
        }
        total = r.data.total_count ?? products.length;
        const pageProducts = r.data.rows.filter((row) => row.subject_kind === "recipe").length;
        if (pageProducts === 0) break;
        offset += CATALOG_PAGE;
      } while (offset < total);
      if (requestId.current !== my) return;
      setRows([...products, ...extras]);
    } catch (err) {
      if (requestId.current !== my) return;
      setLocalError(err instanceof Error ? err.message : "Failed to load COGS grid.");
    } finally {
      if (requestId.current === my) setListBusy(false);
    }
  }, [
    args.enabled,
    args.includeMaterialUses,
    args.includeNamedVariants,
    args.navTick,
    args.plantId,
  ]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return {
    rows,
    setRows,
    columns,
    setColumns,
    series,
    roofStyles,
    sizeBands,
    listBusy,
    localError,
    setLocalError,
    reload,
  };
}
